import { useMutation } from '@apollo/client';
import { Rights } from '@dokucoach/common-types';
import { useContextAuthManager } from 'components/AuthManager';
import ConfirmationModal from 'components/ConfirmationModal';
import { EmptyStateWithText } from 'components/EmptyState';
import AsideListItem from 'components/Layout/components/BaseDataAside/AsideListItem';
import { useContextBaseDataAside } from 'components/Layout/components/BaseDataAside/context';
import EntitySubMenu from 'components/Layout/components/BaseDataAside/EntitySubMenu';
import { BaseDataForm, residentSubmenu } from 'components/Layout/components/BaseDataAside/submenu';
import { TabPane } from 'components/Layout/components/common/styled-components';
import { useContextLayout } from 'components/Layout/context';
import { useContextSnackbar } from 'components/Snackbar/context';
import routePaths from 'constants/route-paths';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { DELETE_RESIDENT, TOGGLE_RESIDENT_ACTIVE_STATE } from 'services/mutation/resident';
import { ResidentPropType } from 'services/residentGroups';
import { AvatarImageType } from 'utils/getAvatarImage';
import { byLastName } from 'utils/sortBy';

const BaseDataResidentsAsideMenu = () => {
  const tabPaneRef = useRef(null);
  const { setActiveUser, setActiveMenu } = useContextBaseDataAside();
  const location = useLocation();
  const { t } = useTranslation();
  const history = useHistory();
  const { filteredResidents, setShouldRefetch } = useContextLayout();
  const { hasPermission } = useContextAuthManager();
  const { showError, showApiError } = useContextSnackbar();

  const [confirmationText, setConfirmationText] = useState<string>('');
  const [showActiveToggleConfirmation, setShowActiveToggleConfirmation] = useState<boolean>(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
  const [selectedResident, setSelectedResident] = useState<ResidentPropType | undefined>();

  const [toggleResidentActiveState] = useMutation(TOGGLE_RESIDENT_ACTIVE_STATE);
  const [deleteResident] = useMutation(DELETE_RESIDENT);

  const userPaths = matchPath(location.pathname, {
    path: routePaths.DATA_RESIDENT,
  }) as { params: { userId: string } };

  const isEntitled = useMemo(
    () => hasPermission(Rights.ADMINISTRATORS.ADMINISTRATOR_ONLY),
    [hasPermission],
  );

  const pathName = location.pathname;

  useEffect(() => {
    const params = {
      userId: userPaths?.params?.userId,
    } as { userId: string };

    if (params.userId) {
      setActiveUser(params.userId);
      if (pathName.includes(routePaths.DATA_CONTACTS)) {
        setActiveMenu(BaseDataForm.CONTACTS);
      } else if (pathName.includes(routePaths.DATA_UTENSILS)) {
        setActiveMenu(BaseDataForm.UTENSILS);
      } else if (pathName.includes(routePaths.DATA_CHECKLIST)) {
        setActiveMenu(BaseDataForm.CHECKLIST);
      } else if (pathName.includes(routePaths.DATA_PASTORAL_CARE)) {
        setActiveMenu(BaseDataForm.PASTORAL_CARE);
      } else if (pathName.includes(routePaths.DATA_ADVICE_CONSULTATION)) {
        setActiveMenu(BaseDataForm.ADVICE_CONSULTATION);
      } else if (pathName.includes(routePaths.DATA_CONTRACT)) {
        setActiveMenu(BaseDataForm.CONTRACT);
      } else if (pathName.includes(routePaths.DATA_INVOICING)) {
        setActiveMenu(BaseDataForm.INVOICING);
      } else if (pathName.includes(routePaths.DATA_DOCUMENT_MANAGEMENT)) {
        setActiveMenu(BaseDataForm.DOCUMENT_MANAGEMENT);
      } else {
        setActiveMenu(BaseDataForm.GENERAL);
      }
    }
  }, [setActiveMenu, setActiveUser, pathName, userPaths]);

  const onToggleActiveState = (resident: ResidentPropType) => {
    const name = `${resident.name.title ? resident.name.title + ' ' : ''}${
      resident.name.firstFull
    } ${resident.name.last}`;

    setConfirmationText(
      resident.isActive
        ? t('resident_inactiveConfirmation', { name })
        : t('resident_activeConfirmation', { name }),
    );

    setShowActiveToggleConfirmation(true);
    setSelectedResident(resident);
  };

  const onDelete = (resident: ResidentPropType) => {
    const name = `${resident.name.title ? resident.name.title + ' ' : ''}${
      resident.name.firstFull
    } ${resident.name.last}`;

    if (resident.isActive) {
      showError('Aktiver Bewohner kann nicht gelöscht werden. Bitte erst inaktiv setzen.');
      return;
    }

    setConfirmationText(t('resident_deleteConfirmation', { name }));
    setShowDeleteConfirmation(true);
    setSelectedResident(resident);
  };

  const handleToggleActiveInactiveResult = async (response: boolean) => {
    setShowActiveToggleConfirmation(false);
    if (!response || !selectedResident) {
      setSelectedResident(undefined);
      return;
    }

    if (selectedResident.isActive) {
      await toggleResidentActiveState({ variables: { id: selectedResident.id, isActive: false } });
      setShouldRefetch('inactive');
      history.push(routePaths.BASE_DATA, routePaths.INACTIVE);
    } else {
      await toggleResidentActiveState({ variables: { id: selectedResident.id, isActive: true } });
      setShouldRefetch('active');
      history.push(routePaths.BASE_DATA);
    }
  };

  const handleDeleteResult = async (response: boolean) => {
    setShowDeleteConfirmation(false);
    if (!response || !selectedResident) {
      setSelectedResident(undefined);
      return;
    }

    try {
      await deleteResident({ variables: { id: selectedResident.id } });
      setShouldRefetch('deleted');
      history.push(routePaths.BASE_DATA, routePaths.DELETED);
    } catch (error) {
      showApiError(error);
    }
  };

  return (
    <>
      <TabPane ref={tabPaneRef}>
        <div className="aside-menu">
          <ul className="menu-nav py-0">
            {filteredResidents.length ? (
              filteredResidents.sort(byLastName).map((resident: ResidentPropType) => (
                <AsideListItem
                  key={resident.id}
                  id={resident.id}
                  label={`${resident.name.last}, ${
                    resident.name.title ? resident.name.title + ' ' : ''
                  }${resident.name.firstFull}`}
                  description={resident?.bed?.label ?? t('no_bed_set')}
                  gender={resident.name.gender}
                  avatarImage={
                    (resident.profilePictureUrl && { url: resident.profilePictureUrl }) || {
                      svg: AvatarImageType.OLD,
                    }
                  }
                  warning={1}
                  tabPaneRef={tabPaneRef}
                  routePath={routePaths.DATA_RESIDENT}
                  isAbsent={resident.isAbsent}
                  showDelete={!resident.deletedAt && !resident.isActive && isEntitled}
                  showRestore={!resident.deletedAt}
                  isDeleted={!resident.isActive}
                  onRestore={() => onToggleActiveState(resident)}
                  onDelete={() => onDelete(resident)}
                >
                  <EntitySubMenu
                    menuItems={residentSubmenu}
                    entityId={resident.id}
                    isInactive={!resident.isActive}
                    isDeleted={!!resident.deletedAt}
                    isApplicant={resident.isApplicant}
                    routePath={routePaths.DATA_RESIDENT}
                  />
                </AsideListItem>
              ))
            ) : (
              <EmptyStateWithText text={t('no_results_for_current_settings')} verticalPositionTop />
            )}
          </ul>
        </div>
      </TabPane>
      <ConfirmationModal
        show={showActiveToggleConfirmation}
        text={confirmationText}
        onResponse={handleToggleActiveInactiveResult}
      />
      <ConfirmationModal
        show={showDeleteConfirmation}
        text={confirmationText}
        onResponse={handleDeleteResult}
      />
    </>
  );
};

export default BaseDataResidentsAsideMenu;
